import { closestCorners, DndContext, DragEndEvent, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AiGenerateButton } from '../../../../widgets/ai';
import { BasicButton, Button, GhostButton, PrimaryButton } from '../../../../widgets/buttons';
import { Icon } from 'src/components/widgets/icon';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import Sparkles from 'src/components/widgets/svg/sparkles';
import MdDragIndicator from 'src/shared/material_icons/action/drag_indicator.svg';
import { PageFooterButtons } from '../../../../widgets/headers';
import { WizardFooterBackButton } from '../wizardFooterBackButton';
import { ContainerBase } from '../../../../widgets/layouts';
import { NotificationBanner } from '../../../../widgets/notificationBanner';
import { ResourceListViewMode, ResourceListViewModeSwitch } from 'src/components/widgets/resourceList';
import _ from 'lodash';
import React, { FunctionComponent, useMemo, useState } from 'react';
import { Section } from 'src/components/widgets/section';
import { SectionContent } from 'src/components/widgets/section/sectionContent';
import { SectionDescription } from 'src/components/widgets/section/sectionDescription';
import { SectionHeader } from 'src/components/widgets/section/sectionHeader';
import { Center } from 'src/shared/globals';
import { T, toLocalID } from 'src/shared/helpers';
import styled from 'styled-components';
import { reorderColorsAction, useProductConfigurationState } from '../../state';
import { ModelColor, isDefaultColor as isDefaultModelColor } from '../../state/model/modelColor';
import { ModelSide } from '../../state/model/modelSide';
import VariantRow from './variantRow';
import Stack from 'src/components/widgets/stack/stack';

// Button that opens the bulk AI generation dialog for the product's variant images and print areas.
const DragHandleButton = styled(GhostButton)`
    color: #455a64;
    cursor: grab;

    &:active {
        cursor: grabbing;
    }
`;

// Wraps each variant in a dnd-kit sortable node and hands a drag handle to its children.
const SortableVariant: React.FC<{ guid: string; children: (dragHandle: React.ReactNode) => React.ReactNode }> = ({ guid, children }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: guid });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1
    };

    const dragHandle = (
        <DragHandleButton
            ref={setActivatorNodeRef}
            type='button'
            $size='small'
            $type='icon'
            aria-label={T._('Drag to reorder', 'Admin')}
            {...attributes}
            {...listeners}
        >
            <Icon>
                <MdDragIndicator />
            </Icon>
        </DragHandleButton>
    );

    return (
        <div ref={setNodeRef} style={style} id={`variant-${guid}`}>
            {children(dragHandle)}
        </div>
    );
};

// In-flow left layout so the quick-access column sits BESIDE the grid (narrowing it), not over it.
const VariantsLayout = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 20px;
    width: 100%;
`;

const VariantsMain = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

// Sticky left column of variant thumbnails: stays in place while the page scrolls; a scrollbar only
// appears if there are more variants than fit the viewport.
const VariantQuickAccessColumn = styled.div`
    position: sticky;
    top: 0;
    align-self: flex-start;
    flex: 0 0 92px;
    width: 92px;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 4px 12px 4px 0;
    border-right: 1px solid #e2e8f0;
`;

const VariantThumb = styled.button`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 6px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease;

    &:hover {
        border-color: ${(props) => props.theme.app.primary};
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }
`;

const VariantThumbImage = styled.div`
    width: 100%;
    height: 44px;
    border-radius: 4px;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }
`;

const VariantThumbName = styled.span`
    font-size: 10px;
    font-weight: 600;
    color: #37474f;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
`;

// Grid variant card with reduced top/bottom padding.
const VariantCard = styled(Section)`
    ${SectionContent} {
        padding-top: 12px;
        padding-bottom: 12px;
    }
`;

// Compact header (the "Set images..." intro) — no white card, reduced padding and smaller description.
const CompactHeaderSection = styled(Section)`
    background: transparent;
    border: none;
    border-radius: 0;

    ${SectionHeader} {
        padding: 4px 0;
    }
    ${SectionContent} {
        padding: 0;
    }
    ${SectionDescription} {
        font-size: 13px;
        margin-bottom: 0;
    }
`;

interface VariantsListProps {
    isSidesPerVariantEnabled: boolean;
    onSideAreaChangeClick: (modelSide: ModelSide, modelColor: ModelColor) => void;
    onSideImageChangeClick: (modelSide: ModelSide, modelColor: ModelColor) => void;
    onSideAiGenerateClick: (modelSide: ModelSide, modelColor: ModelColor) => void;
    onBulkGenerateClick: () => void;
    onSideToggleClick: (colorGuid: string, sideGuid: string) => void;
    onVariantRemoveClick: (modelColor: ModelColor) => void;
    onVariantEditClick: (modelColor: ModelColor) => void;
    onVariantAddClick: (showConnectModelDialog?: boolean) => void;
    onPreviewClick: () => void;
    onBackClick: () => void;
    onSetMandatorySide: (value: boolean, modelSide: ModelSide) => void;
    onSetRotateSide: (value: boolean, model: ModelSide) => void;
    onClickJsonSettings: (modelColor: ModelColor) => void;
    unsupportedSalesChannelsVariantsIDs: string[];
    hasMappingMismatch: boolean;
    mismatchedVariantGuids: Set<string>;
    fixedVariantGuids: Set<string>;
    storeVariantsRemoved: boolean;
}

const VariantsList: FunctionComponent<VariantsListProps> = ({
    isSidesPerVariantEnabled,
    onSideImageChangeClick,
    onSideAiGenerateClick,
    onBulkGenerateClick,
    onSideAreaChangeClick,
    onSideToggleClick,
    onVariantEditClick,
    onVariantRemoveClick,
    onVariantAddClick,
    onPreviewClick,
    onBackClick,
    onSetMandatorySide,
    onSetRotateSide,
    onClickJsonSettings,
    unsupportedSalesChannelsVariantsIDs,
    hasMappingMismatch,
    mismatchedVariantGuids,
    fixedVariantGuids,
    storeVariantsRemoved
}) => {
    const { state, dispatch } = useProductConfigurationState();
    const [viewMode, setViewMode] = useState<ResourceListViewMode>('cards');
    const colors = useMemo(
        () =>
            _.sortBy(
                state.product.colors.filter((x) => !isDefaultModelColor(x)),
                (color) => color.position
            ),
        [state.product.colors]
    );

    const additionalCodesFiltered = useMemo(() => {
        return state.product.additionalCodes.filter((ac) => {
            return !unsupportedSalesChannelsVariantsIDs.some((x) => toLocalID(x) === toLocalID(ac.salesChannelId));
        });
    }, [state.product.additionalCodes, unsupportedSalesChannelsVariantsIDs]);

    const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { delay: 50, tolerance: 5 } }), useSensor(TouchSensor));

    const handleVariantDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const guids = colors.map((c) => c.guid);
        const oldIndex = guids.indexOf(String(active.id));
        const newIndex = guids.indexOf(String(over.id));
        if (oldIndex === -1 || newIndex === -1) return;

        dispatch(reorderColorsAction(arrayMove(guids, oldIndex, newIndex)));
    };

    const scrollToVariant = (guid: string) => {
        document.getElementById(`variant-${guid}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const getFirstImage = (color: ModelColor) => color.sides.map((s) => s.imageUrl).find((url) => !!url);

    const renderVariant = (modelColor: ModelColor, dragHandle: React.ReactNode) => (
        <VariantRow
            color={modelColor}
            dragHandle={dragHandle}
            viewMode={viewMode}
            showEditButton={true}
            onClickJsonSettings={() => onClickJsonSettings(modelColor)}
            isSidesPerVariantEnabled={isSidesPerVariantEnabled}
            onToggleClick={onSideToggleClick}
            onAreaChangeClick={onSideAreaChangeClick}
            onImageChangeClick={onSideImageChangeClick}
            onAiGenerateClick={onSideAiGenerateClick}
            onRemoveClick={() => onVariantRemoveClick(modelColor)}
            onEditClick={() => onVariantEditClick(modelColor)}
            onSetMandatorySide={onSetMandatorySide}
            onSetRotateSide={onSetRotateSide}
            highlightEdit={mismatchedVariantGuids.has(modelColor.guid) && !fixedVariantGuids.has(modelColor.guid)}
            showRemoveButton={!(state.product.colors.length == 1 && state.product.colors[0].colorID == 1)}
        />
    );

    return (
        <React.Fragment>
            <ContainerBase>
                <Stack>
                    {hasMappingMismatch && (
                        <NotificationBanner
                            type='WARNING'
                            title={T._('Variant mapping mismatch detected', 'Admin')}
                            content={T._(
                                'The variant options in your store appear to have changed since the last configuration. The current mapping may be incorrect and could cause out-of-stock errors. Please fix the variants highlighted by the flashing edit icon.',
                                'Admin'
                            )}
                        />
                    )}

                    {storeVariantsRemoved && (
                        <NotificationBanner
                            type='WARNING'
                            title={T._('Store variants not found', 'Admin')}
                            content={T._(
                                'It seems that all the variants for this product have been removed from your store. By keeping the variants configured in Zakeke, customers may still be asked to choose a variant that will appear as out-of-stock, causing a poor user experience. We recommend re-adding the variants in your store or removing the variant configuration in Zakeke.',
                                'Admin'
                            )}
                        />
                    )}

                    {colors.length > 0 ? (
                        <>
                            <CompactHeaderSection
                                headerActions={
                                    <>
                                        <AiGenerateButton type='button' onClick={onBulkGenerateClick}>
                                            <Sparkles />
                                            {T._('Generate variant images and/or print areas with AI', 'Admin')}
                                        </AiGenerateButton>
                                        <BasicButton disabled={storeVariantsRemoved} onClick={() => onVariantAddClick()}>
                                            {state.ecommerceSalesChannel.some((e) => e === 'NONE') ? T._('Edit attributes', 'Admin') : T._('Add', 'Admin')}
                                        </BasicButton>
                                        {additionalCodesFiltered.length > 0 && (
                                            <BasicButton onClick={() => onVariantAddClick(true)}>{T._('Edit mapping', 'Admin')}</BasicButton>
                                        )}
                                        <ResourceListViewModeSwitch
                                            viewMode={viewMode}
                                            viewModeLabel={T._('View mode', 'Admin')}
                                            cardsViewLabel={T._('Grid view', 'Admin')}
                                            tableViewLabel={T._('List view', 'Admin')}
                                            onViewModeChange={setViewMode}
                                        />
                                    </>
                                }
                            />

                            <VariantsLayout>
                                {colors.length > 1 && viewMode === 'cards' && (
                                    <VariantQuickAccessColumn>
                                        {colors.map((modelColor) => {
                                            const img = getFirstImage(modelColor);
                                            return (
                                                <VariantThumb
                                                    key={modelColor.guid}
                                                    type='button'
                                                    onClick={() => scrollToVariant(modelColor.guid)}
                                                    title={modelColor.name}
                                                >
                                                    <VariantThumbImage>{img && <img src={img} alt={modelColor.name} />}</VariantThumbImage>
                                                    <VariantThumbName>{modelColor.name}</VariantThumbName>
                                                </VariantThumb>
                                            );
                                        })}
                                    </VariantQuickAccessColumn>
                                )}

                                <VariantsMain>
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCorners}
                                        modifiers={[restrictToVerticalAxis]}
                                        onDragEnd={handleVariantDragEnd}
                                    >
                                        <SortableContext items={colors.map((c) => c.guid)} strategy={verticalListSortingStrategy}>
                                            {viewMode === 'table' ? (
                                                // List mode: one Section wrapping a tight list of rows (like the customizable products list).
                                                <Section>
                                                    {colors.map((modelColor) => (
                                                        <SortableVariant key={modelColor.guid} guid={modelColor.guid}>
                                                            {(dragHandle) => renderVariant(modelColor, dragHandle)}
                                                        </SortableVariant>
                                                    ))}
                                                </Section>
                                            ) : (
                                                // Grid mode: each variant in its own card/Section.
                                                colors.map((modelColor) => (
                                                    <SortableVariant key={modelColor.guid} guid={modelColor.guid}>
                                                        {(dragHandle) => <VariantCard>{renderVariant(modelColor, dragHandle)}</VariantCard>}
                                                    </SortableVariant>
                                                ))
                                            )}
                                        </SortableContext>
                                    </DndContext>
                                </VariantsMain>
                            </VariantsLayout>
                        </>
                    ) : (
                        <Section>
                            <div
                                css={`
                                    margin: 40px 0;
                                `}
                            >
                                <Center>
                                    <PrimaryButton $size='large' onClick={() => onVariantAddClick()}>
                                        <Icon>
                                            <PlusIcon />
                                        </Icon>
                                        {state.ecommerceSalesChannel.some((e) => e === 'NONE') && state.ecommerceSalesChannel.length == 1
                                            ? T._('Edit attributes', 'Admin')
                                            : T._('Add variant', 'Admin')}
                                    </PrimaryButton>
                                </Center>
                            </div>
                        </Section>
                    )}
                </Stack>
            </ContainerBase>

            {/* {state.product.additionalCodes.length == 0 && colors.length > 0 && <BasicButton onClick={() => onVariantAddClick()}>{(state.ecommerceSalesChannel.some(e=>e === 'NONE') && state.ecommerceSalesChannel.length == 1) ? T._('Go to variations','Admin') : T._('Add', 'Admin')}</BasicButton>} */}
            {colors.length > 0 && (
                <PageFooterButtons $fixed={true} fullWidth leftContainerContent={<WizardFooterBackButton onClick={onBackClick} />}>
                    <Button $size='large' onClick={onPreviewClick}>
                        {T._(`Go to Review & Save`, 'Admin')}
                    </Button>
                </PageFooterButtons>
            )}
        </React.Fragment>
    );
};

export default VariantsList;
